import React, { useState, useEffect, useRef } from 'react';
import { pinnedViewsAPI } from '../services/api';
import { toast } from 'sonner';
import { Bookmark, Trash2, Plus, ChevronDown, Check, Star } from 'lucide-react';

/**
 * Per-user "Pinned Views" dropdown for project boards.
 *
 * Props
 *  - board: one of 'open' | 'closed' | 'ar' | 'dead'
 *  - currentState: object describing the current filter/search/sort UI state
 *  - onApply(state): caller restores its UI from the supplied state object
 *
 * The component owns nothing except its own list + open/save UI; the parent
 * board is responsible for serializing its filters into `currentState` and
 * for re-hydrating itself when `onApply` fires.
 *
 * If one of the views is flagged `is_default`, it is auto-applied the first
 * time the user lands on the board this browser session. Subsequent
 * navigations within the same session use whatever filters the user has
 * since edited (tracked via the `usePersistedState` sessionStorage on the
 * parent board).
 */
const PinnedViews = ({ board, currentState, onApply }) => {
  const [views, setViews] = useState([]);
  const [open, setOpen] = useState(false);
  const [savePromptOpen, setSavePromptOpen] = useState(false);
  const [newName, setNewName] = useState('');
  const [activeId, setActiveId] = useState(null);
  const containerRef = useRef(null);
  // Latest onApply so the auto-apply effect doesn't re-fire whenever the
  // parent re-renders with a new closure.
  const onApplyRef = useRef(onApply);
  useEffect(() => { onApplyRef.current = onApply; }, [onApply]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const data = await pinnedViewsAPI.list(board);
        if (cancelled) return;
        const list = data.views || [];
        setViews(list);

        // Auto-apply default view, but only once per browser-tab session
        // per board. That way it kicks in on first arrival but a PM who's
        // mid-task and navigates into a project + back keeps their current
        // filters (which usePersistedState already restored).
        const sessionKey = `restorepm:default-applied:${board}`;
        if (!sessionStorage.getItem(sessionKey)) {
          const def = list.find((v) => v.is_default);
          if (def) {
            sessionStorage.setItem(sessionKey, '1');
            setActiveId(def.id);
            onApplyRef.current?.(def.state || {});
          }
        }
      } catch {
        /* unauthenticated or transient — just leave list empty */
      }
    })();
    return () => { cancelled = true; };
  }, [board]);

  useEffect(() => {
    const onDocClick = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setOpen(false);
        setSavePromptOpen(false);
      }
    };
    document.addEventListener('mousedown', onDocClick);
    return () => document.removeEventListener('mousedown', onDocClick);
  }, []);

  const handleApply = (view) => {
    setActiveId(view.id);
    setOpen(false);
    onApply(view.state || {});
  };

  const handleSave = async () => {
    const name = newName.trim();
    if (!name) {
      toast.error('Please enter a name for this view.');
      return;
    }
    try {
      const data = await pinnedViewsAPI.create(board, name, currentState);
      const saved = data.view;
      setViews((prev) => {
        const without = prev.filter((v) => v.id !== saved.id && v.name !== saved.name);
        return [...without, saved].sort((a, b) => a.name.localeCompare(b.name));
      });
      setActiveId(saved.id);
      setNewName('');
      setSavePromptOpen(false);
      setOpen(false);
      toast.success(`Saved view "${saved.name}"`);
    } catch (err) {
      const msg = err?.response?.data?.detail || 'Failed to save view';
      toast.error(msg);
    }
  };

  const handleDelete = async (view, e) => {
    e.stopPropagation();
    if (!window.confirm(`Delete pinned view "${view.name}"?`)) return;
    try {
      await pinnedViewsAPI.delete(view.id);
      setViews((prev) => prev.filter((v) => v.id !== view.id));
      if (activeId === view.id) setActiveId(null);
      toast.success('View deleted');
    } catch {
      toast.error('Failed to delete view');
    }
  };

  const handleToggleDefault = async (view, e) => {
    e.stopPropagation();
    const nextDefault = !view.is_default;
    try {
      await pinnedViewsAPI.setDefault(view.id, nextDefault);
      setViews((prev) => prev.map((v) => {
        if (v.id === view.id) return { ...v, is_default: nextDefault };
        // Only one default per board, so clear the flag everywhere else
        // when we're setting a new one.
        if (nextDefault) return { ...v, is_default: false };
        return v;
      }));
      // If the user is enabling a default, remember that this session has
      // satisfied its auto-apply so we don't fight them on the next visit.
      if (nextDefault) {
        sessionStorage.setItem(`restorepm:default-applied:${board}`, '1');
      }
      toast.success(nextDefault ? `Default set to "${view.name}"` : 'Default cleared');
    } catch {
      toast.error('Failed to update default');
    }
  };

  const activeView = views.find((v) => v.id === activeId);

  return (
    <div ref={containerRef} className="relative inline-block" data-testid={`pinned-views-${board}`}>
      <button
        type="button"
        onClick={() => { setOpen((o) => !o); setSavePromptOpen(false); }}
        className="flex items-center gap-2 border border-gray-300 bg-white hover:bg-gray-50 text-gray-700 px-3 py-2 rounded transition-colors text-sm"
        data-testid={`pinned-views-toggle-${board}`}
      >
        <Bookmark className="w-4 h-4 text-[#5B8DB8]" />
        <span className="truncate max-w-[160px]">
          {activeView ? activeView.name : 'Pinned Views'}
        </span>
        <ChevronDown className="w-4 h-4 text-gray-400" />
      </button>

      {open && (
        <div
          className="absolute z-30 mt-1 w-80 bg-white border border-gray-200 rounded shadow-lg overflow-hidden"
          data-testid={`pinned-views-menu-${board}`}
        >
          <div className="max-h-72 overflow-y-auto">
            {views.length === 0 ? (
              <div className="px-3 py-3 text-sm text-gray-500 italic">No saved views yet.</div>
            ) : (
              views.map((view) => (
                <div
                  key={view.id}
                  onClick={() => handleApply(view)}
                  className={`flex items-center justify-between gap-2 px-3 py-2 text-sm cursor-pointer hover:bg-gray-50 ${activeId === view.id ? 'bg-[#E8F7F9]' : ''}`}
                  data-testid={`pinned-view-item-${view.id}`}
                >
                  <div className="flex items-center gap-2 min-w-0">
                    {activeId === view.id ? (
                      <Check className="w-4 h-4 text-[#4ECFDB] flex-shrink-0" />
                    ) : (
                      <Bookmark className="w-4 h-4 text-gray-400 flex-shrink-0" />
                    )}
                    <span className="truncate text-gray-700">{view.name}</span>
                  </div>
                  <div className="flex items-center gap-1 flex-shrink-0">
                    <button
                      type="button"
                      onClick={(e) => handleToggleDefault(view, e)}
                      className={`transition-colors ${view.is_default ? 'text-amber-400 hover:text-amber-500' : 'text-gray-300 hover:text-amber-400'}`}
                      title={view.is_default ? 'Default on load — click to clear' : 'Set as default on load'}
                      data-testid={`pinned-view-default-${view.id}`}
                    >
                      <Star className="w-4 h-4" fill={view.is_default ? 'currentColor' : 'none'} />
                    </button>
                    <button
                      type="button"
                      onClick={(e) => handleDelete(view, e)}
                      className="text-gray-400 hover:text-red-500 transition-colors"
                      title="Delete view"
                      data-testid={`pinned-view-delete-${view.id}`}
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              ))
            )}
          </div>

          <div className="border-t border-gray-200 bg-gray-50">
            {savePromptOpen ? (
              <div className="p-2 flex items-center gap-2">
                <input
                  autoFocus
                  type="text"
                  value={newName}
                  onChange={(e) => setNewName(e.target.value)}
                  onKeyDown={(e) => { if (e.key === 'Enter') handleSave(); }}
                  placeholder="View name..."
                  maxLength={80}
                  className="flex-1 border border-gray-300 rounded px-2 py-1 text-sm focus:outline-none focus:ring-1 focus:ring-[#4ECFDB]"
                  data-testid={`pinned-view-name-input-${board}`}
                />
                <button
                  type="button"
                  onClick={handleSave}
                  className="bg-[#4ECFDB] hover:bg-[#3AB8C5] text-white px-3 py-1 rounded text-sm"
                  data-testid={`pinned-view-save-confirm-${board}`}
                >
                  Save
                </button>
              </div>
            ) : (
              <button
                type="button"
                onClick={() => setSavePromptOpen(true)}
                className="w-full flex items-center gap-2 px-3 py-2 text-sm text-[#5B8DB8] hover:bg-gray-100 transition-colors"
                data-testid={`pinned-view-save-current-${board}`}
              >
                <Plus className="w-4 h-4" />
                Save current view…
              </button>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default PinnedViews;
